import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { LookupModel } from '../../models/common/lookup.model';
import { MailTemplateSendModel } from '../../models/mail/mail-template-send.model';
import { MailTemplateModel } from '../../models/mail/mail-template.model';
import { PreviewMailModel } from '../../models/mail/preview-mail.model';
import { SendMailModel } from '../../models/mail/send-mail.model';
import { CommunicationLogPagingGetDto, CommunicationLogLookupsModel, CommunicationLogDto } from '../models/communication-log.model';
import { SentMailLogModel } from '../models/sent-mail-log.model';
import { SpinnerService } from '../services/spinner.service';

@Injectable({
  providedIn: 'root',
})
export class MailService {
  public baseUrl = environment.api2BaseUrl;
  public authApiUrl = environment.apiAuthUrl;
  public onError: BehaviorSubject<string> = new BehaviorSubject('');
  public serviceParameter = {
    'Content-Type': 'application/json; charset=utf-8',
    Accept: 'application/json',
    Authorization: '',
  };

  public constructor(private readonly http: HttpClient, private spinnerService: SpinnerService) { }

  public getMailMemberTemplates(memberIds: string): Observable<any> {
    this.spinnerService.addRequest('getMailMemberTemplates'); // Show spinner
    return this.http
      .post<any>(`${this.baseUrl}/api/Mail/MailTemplate`, { memberIds: memberIds }, {
        withCredentials: false,
        observe: 'response',
      })
      .pipe(
        map((res: any) => {
          if (res.error) {
            throw new Error(res.error);
          }
          return res.body;
        }),
        catchError((error: Error) => {
          this.onError.next(error.message);
          return throwError(error);
        }),
        finalize(() => this.spinnerService.popRequest('getMailMemberTemplates')) // Hide spinner on completion
      );
  }

  public getMailTemplate(id: number): Observable<MailTemplateModel> {
    this.spinnerService.addRequest('getMailTemplate'); // Show spinner
    return this.http
      .get<MailTemplateModel>(`${this.baseUrl}/api/Mail/${id}/MailTemplate`, {
        withCredentials: false,
        observe: 'response',
      })
      .pipe(
        map((res: any) => {
          if (res.error) {
            throw new Error(res.error);
          }
          return res.body;
        }),
        catchError((error: Error) => {
          this.onError.next(error.message);
          return throwError(error);
        }),
        finalize(() => this.spinnerService.popRequest('getMailTemplate')) // Hide spinner on completion
      );
  }

  public saveMailTemplate(template: MailTemplateModel): Observable<LookupModel[]> {
    this.spinnerService.addRequest('saveMailTemplate'); // Show spinner
    return this.http
      .post<LookupModel[]>(`${this.baseUrl}/api/Mail/SaveMailTemplate`, template, {
        withCredentials: false,
        observe: 'response',
      })
      .pipe(
        map((res: any) => {
          if (res.error) {
            throw new Error(res.error);
          }
          return res.body;
        }),
        catchError((error: Error) => {
          this.onError.next(error.message);
          return throwError(error);
        }),
        finalize(() => this.spinnerService.popRequest('saveMailTemplate')) // Hide spinner on completion
      );
  }

  public previewMail(template: PreviewMailModel): Observable<PreviewMailModel> {
    this.spinnerService.addRequest('previewMail'); // Show spinner
    return this.http
      .post<PreviewMailModel>(`${this.baseUrl}/api/Mail/PreviewMail`, template, {
        withCredentials: false,
        observe: 'response',
      })
      .pipe(
        map((res: any) => {
          if (res.error) {
            throw new Error(res.error);
          }
          return res.body;
        }),
        catchError((error: Error) => {
          this.onError.next(error.message);
          return throwError(error);
        }),
        finalize(() => this.spinnerService.popRequest('previewMail')) // Hide spinner on completion
      );
  }

  public getSentMailLog(sentMailLogId: number): Observable<SentMailLogModel> {
    this.spinnerService.addRequest('getSentMailLog'); // Show spinner
    return this.http
      .get<SentMailLogModel>(`${this.baseUrl}/api/Mail/${sentMailLogId}/SentMailLog`, {
        withCredentials: false,
        observe: 'response',
      })
      .pipe(
        map((res: any) => {
          if (res.error) {
            throw new Error(res.error);
          }
          return res.body;
        }),
        catchError((error: Error) => {
          this.onError.next(error.message);
          return throwError(error);
        }),
        finalize(() => this.spinnerService.popRequest('getSentMailLog')) // Hide spinner on completion
      );
  }

  public getCommunincationLogs(log: CommunicationLogPagingGetDto): Observable<CommunicationLogDto> {
    this.spinnerService.addRequest('getCommunincationLogs'); // Show spinner
    return this.http
      .post<CommunicationLogDto>(`${this.baseUrl}/api/CommunicationLog`, log, {
        withCredentials: false,
        observe: 'response',
      })
      .pipe(
        map((res: any) => {          
          if (res.error) {
            throw new Error(res.error);
          }
          return res.body;
        }),
        catchError((error: Error) => {
          this.onError.next(error.message);
          return throwError(() => error);
        }),
        finalize(() => this.spinnerService.popRequest('getCommunincationLogs')) // Hide spinner on completion
      );
  }

  public sendNotificationVet(model: any): Observable<{ data: boolean; warnings?: string[] }> {
    this.spinnerService.addRequest('sendNotificationVet'); // Show spinner
    return this.http
      .post<boolean>(`${this.baseUrl}/api/Notifications/NotificationVet`, model, {
        withCredentials: false,
        observe: 'response',
      })
      .pipe(
        map((res) => {

          const responseBody = res.body as any;

          // Check if there are any warnings in the response
          if (responseBody?.warnings && responseBody.warnings.length > 0) {
            // Log warnings or handle them as needed

            // Optionally, you could return both data and warnings in a single response object
            return {
              data: responseBody.data,
              warnings: responseBody.warnings,
            };
          }

          // Return just the data if no warnings are present
          return { data: responseBody };
        }),
        catchError((error: Error) => {
          this.onError.next(error.message);
          return throwError(error);
        }),
        finalize(() => this.spinnerService.popRequest('sendNotificationVet')) // Hide spinner on completion
      );
  }

  public saveReplyToEmailAddress(emailAddress: string): Observable<boolean> {
    this.spinnerService.addRequest('saveReplyToEmailAddress'); // Show spinner
    return this.http
      .post<boolean>(`${this.authApiUrl}/users/saveUserReplyToAddress`, { replyToEmailAddress: emailAddress }, {
        withCredentials: false,
        observe: 'response',
      })
      .pipe(
        map((res) => res.body),
        catchError((error: Error) => {
          this.onError.next(error.message);
          return throwError(error);
        }),
        finalize(() => this.spinnerService.popRequest('saveReplyToEmailAddress')) // Hide spinner on completion
      );
  }

  public getCommunicationLogLookups(): Observable<CommunicationLogLookupsModel> {
    this.spinnerService.addRequest('getCommunicationLogLookups'); // Show spinner
    return this.http
      .get<CommunicationLogLookupsModel>(`${this.baseUrl}/api/communicationlog/lookups`, {
        withCredentials: false,
        observe: 'response',
      })
      .pipe(
        map((res) => res.body),
        catchError((error: Error) => {
          this.onError.next(error.message);
          return throwError(error);
        }),
        finalize(() => this.spinnerService.popRequest('getCommunicationLogLookups')) // Hide spinner on completion
      );
  }
}
